import {
  DEFAULT_CCTV_MAPPING,
  DEFAULT_CONTROLLER_MAPPING,
  DEFAULT_ROBOT_MAPPING,
  type ControllerMapping,
} from '@/lib/controller/gamepad-core'
import { authApi } from '@/services/api/domains/auth.api'
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'

import type { ViewType } from '../components/organisms/Map/types'
import { subscribeSessionExpired } from '../services/api/session-expired-emitter'
import { ApiRequestError } from '../services/api/types'
import * as authService from '../services/auth'
import { rotateAnonymousId } from '../services/auth/anonymous-id'
import { consumeHandoffIntoLocalStorage, handOffTo } from '../services/auth/handoff'
import { TOKEN_REFRESH_RETRY_MS, refreshDelayMs } from '../services/auth/valid-access-token'

export type AuthState =
  | 'authenticated'
  | 'unauthenticated'
  | 'loading'
  | 'tenant_suspended'
  | 'tenant_deleted'
  // Issue #259 — pre-flight tenant existence check failed at login time; the
  // subdomain doesn't correspond to any tenant in the DB. AuthGuard redirects
  // to /account-not-found.
  | 'tenant_not_found'

export interface User {
  id: string
  email: string
  name: string
  role: 'admin' | 'publisher' | 'viewer'
  tenantId: string
  tenantName: string
  assignedModules: string[]
  /**
   * Per-tenant role allocation. Populated from `tenant.userLimits` on `/users/me`
   * for tenant-scoped callers; null for central-domain callers (use the central
   * license maxUsers there instead). Drives the Settings/Users capacity cards.
   */
  tenantUserLimits: { admin: number; publisher: number; viewer: number } | null
  licenseExpiresAt: string | null
  licenses: string[]
  mfaEnabled?: boolean
  isGuest?: boolean
  /**
   * Central Organization's deployment mode, surfaced from `/users/me` (Plan 6a
   * Task 8). RETAINED for consumers/telemetry but no longer drives any UI — the
   * amber "On-Prem" `AppHeader` pill was removed in #706. Defaults to `'cloud'`
   * when the backend omits the field.
   */
  deploymentMode: 'cloud' | 'onprem'
  /**
   * Task 3 — effective sitewide-assistant availability: the AND of the
   * operator's per-tenant flag and the tenant admin's own opt-out (see
   * `services/api/domains/auth.api.ts::User.assistantEnabled`). Absent means
   * enabled — defaulted defensively to `true` in `fetchUserProfile` below,
   * same as `deploymentMode`, so a returning session with a stale cached user
   * (see the localStorage rehydration path) never starts with the assistant
   * wrongly hidden.
   */
  assistantEnabled: boolean
  preferences: UserPreferences
}

export interface UserPreferences {
  darkMode: boolean
  locale: string
  sidebarCollapsed: boolean
  /**
   * Last-toggled map view-type. Used as a fallback in `resolveInitialViewType`
   * when neither the URL `?view=` param nor saved-map metadata specifies one.
   * Persisted server-side via `authApi.updatePreferences`.
   */
  preferredViewType: ViewType
  /**
   * Gamepad mapping for dashboard controller support (Settings → Controller).
   * Saved as a COMPLETE object on every change — the backend `$set` replaces
   * the whole sub-object rather than deep-merging nested keys.
   */
  controllerMapping?: ControllerMapping
}

interface AuthContextValue {
  user: User | null
  isAuthenticated: boolean
  authState: AuthState
  isLoading: boolean
  error: string | null
  signIn: (email: string, password: string) => Promise<void>
  respondMfaChallenge: (email: string, code: string, session: string) => Promise<void>
  respondNewPasswordChallenge: (email: string, newPassword: string, session: string) => Promise<void>
  signOut: () => Promise<void>
  /**
   * End a session the identity provider has ALREADY rejected, locally only. Unlike signOut it
   * never contacts the provider: Cognito's signOut is a GlobalSignOut, which would revoke the
   * user's sessions on every device.
   */
  clearLocalSession: () => void
  checkAuth: () => Promise<void>
  updatePreferences: (prefs: Partial<UserPreferences>) => void
  clearError: () => void
  refreshUser: () => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

const TOKEN_KEY = 'gisify_token'
const USER_KEY = 'gisify_user'
// The refresh material both auth clients keep — and drop ONLY on a definitive
// provider rejection. Its presence is the single provider-agnostic "may this
// session be torn down?" signal, shared with `services/api/client.ts`.
const REFRESH_TOKEN_KEY = 'gisify_refresh_token'
/**
 * REPLACED by `refreshDelayMs`, which reads the token's own `exp`.
 *
 * A flat 45 minutes was chosen for Cognito (1-hour access token, 30-day refresh token). On-prem the
 * provider is Keycloak, whose realm here answers `accessTokenLifespan = 300` and
 * `ssoSessionIdleTimeout = 1800` — so this timer first fired a quarter of an hour AFTER the SSO
 * session had already idled out, and could never once keep a session alive. See
 * `services/auth/valid-access-token.ts` for the measurement.
 */

const DEFAULT_PREFS: UserPreferences = {
  darkMode: false,
  locale: 'en',
  sidebarCollapsed: false,
  preferredViewType: '2d',
  // Phase 5: `robot` (and later `cctv`) are spread in HERE rather than
  // embedded in DEFAULT_CONTROLLER_MAPPING — the Phase 1 provider (and its
  // specs) treat that constant as exactly {deadmanButton, invertTilt,
  // swapSticks}, so embedding would change provider behavior/assertions.
  // Consumers deep-merge via `resolveRobotMapping`/`resolveCctvMapping`
  // anyway.
  controllerMapping: { ...DEFAULT_CONTROLLER_MAPPING, robot: DEFAULT_ROBOT_MAPPING, cctv: DEFAULT_CCTV_MAPPING },
}

/**
 * #218 — When a tenant user signs in via the central root domain
 * (`console.{baseDomain}`), redirect to their tenant subdomain
 * (`<tenantId>.console.{baseDomain}`) preserving the current path/query/hash.
 * Returns true if a redirect was triggered (caller should halt). On localhost,
 * non-matching hosts, or central-admin users (no tenantId), returns false.
 */
/**
 * #238 — when /users/me throws 403 "Tenant suspended" / "Tenant deleted" after
 * a successful Cognito auth, drop the half-issued local session, route to the
 * account-status authState (for active sessions where AuthGuard is mounted),
 * AND surface a clear human-readable message via setError so the login screen
 * can show *why* the attempt was rejected instead of the generic
 * "Sign in failed" toast.
 *
 * Returns true when the error matched. Callers should `throw err` after, so
 * the login form's existing catch keeps the user on the page with the message
 * visible (instead of `await signIn()` resolving and navigate(from) running).
 */
function mapTenantStatusError(
  err: unknown,
  setAuthState: (s: AuthState) => void,
  setError: (s: string) => void,
): boolean {
  if (!(err instanceof ApiRequestError) || err.status !== 403) return false
  if (err.message !== 'Tenant suspended' && err.message !== 'Tenant deleted') return false
  // Drop the partial session so a refresh doesn't loop the user back through
  // checkAuth → /users/me → 403 → here.
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
  setAuthState(err.message === 'Tenant suspended' ? 'tenant_suspended' : 'tenant_deleted')
  setError(
    err.message === 'Tenant suspended'
      ? 'This account has been suspended. Please contact your administrator to restore access.'
      : 'This account no longer exists. Please contact support@gisify.com.',
  )

  return true
}

function enforceTenantSubdomain(tenantId: string | null | undefined): boolean {
  if (!tenantId) return false
  if (typeof window === 'undefined') return false
  const baseDomain = (import.meta.env.VITE_BASE_DOMAIN as string | undefined) || 'gisify.dev'
  const host = window.location.hostname
  // Only redirect from the central root host. Localhost / IP / other hosts are
  // dev-mode and don't have tenant subdomains; cross-tenant subdomain logins
  // are caught by `mapTenantMismatchError` against the backend's #218 gate.
  if (host !== `console.${baseDomain}`) return false
  const url = new URL(window.location.href)
  url.hostname = `${tenantId}.console.${baseDomain}`
  // Land on root so AuthGuard runs checkAuth (which consumes the handoff)
  // even if the source page was a public route like `/auth/login`.
  url.pathname = '/'
  // #218 — carry the just-minted Cognito tokens in the URL fragment so the
  // destination subdomain can hydrate localStorage on first paint, sparing
  // the user from re-typing credentials. URL fragments don't traverse the
  // network and have no 4KB cookie limit (the Cognito triplet is ~5.4KB
  // base64-encoded — too large for cookies, fine for fragments).
  // handOffTo also clears the session from THIS origin: a root that kept its
  // copy signed the user back in after they signed out on the tenant.
  handOffTo(url)

  return true
}

/**
 * #218 — when /users/me returns 403 "Tenant mismatch" (the caller's tenantId
 * differs from the subdomain's tenantId), redirect to the correct subdomain.
 * Cognito has already minted tokens for this user, so we ship them across via
 * the handoff cookie and the destination console picks up the session without
 * re-prompting.
 *
 * Returns true on match — caller should `return` (page is being replaced; no
 * further state mutation makes sense).
 */
function mapTenantMismatchError(err: unknown): boolean {
  if (!(err instanceof ApiRequestError) || err.status !== 403) return false
  if (err.message !== 'Tenant mismatch') return false
  const correctTenantId = (err.details as { correctTenantId?: string } | undefined)?.correctTenantId
  if (!correctTenantId) return false
  if (typeof window === 'undefined') return false

  const baseDomain = (import.meta.env.VITE_BASE_DOMAIN as string | undefined) || 'gisify.dev'
  const url = new URL(window.location.href)
  url.hostname = `${correctTenantId}.console.${baseDomain}`
  url.pathname = '/'
  // Moves the session: the wrong subdomain must not keep a copy either.
  handOffTo(url)

  return true
}

async function fetchUserProfile(): Promise<User> {
  const response = await authApi.getMe()
  const apiUser = response.data

  const storedUser = localStorage.getItem(USER_KEY)
  const storedPreferences: Partial<UserPreferences> | null = storedUser ? JSON.parse(storedUser).preferences : null

  // Prefer backend preferences so the choice persists across devices; fall back
  // to localStorage (useful right after a toggle before the server round-trip
  // completes), then to sensible defaults.
  const preferences: UserPreferences = {
    ...DEFAULT_PREFS,
    ...(storedPreferences ?? {}),
    ...(apiUser.preferences ?? {}),
  }

  return {
    id: apiUser.id,
    email: apiUser.email,
    name: apiUser.name,
    role: apiUser.role,
    tenantId: apiUser.tenantId,
    tenantName: apiUser.tenantName || apiUser.tenantId,
    assignedModules: apiUser.assignedModules ?? [],
    tenantUserLimits: apiUser.tenantUserLimits ?? null,
    licenseExpiresAt: apiUser.licenseExpiresAt ?? null,
    // licenses is the legacy alias used by AppHeader requiredLicense + LicenseGuard requiredLicenses
    // gating; mirror assignedModules so license-gated nav and routes work.
    licenses: apiUser.assignedModules ?? [],
    mfaEnabled: apiUser.mfaEnabled,
    isGuest: apiUser.isGuest ?? false,
    // Plan 6a Task 12 — default to 'cloud' when backend omits the field
    // (legacy Organization docs predating Plan 6a Task 8's schema migration).
    // Same default the backend applies in `services/console/users/src/handlers/me.ts`.
    deploymentMode: apiUser.deploymentMode ?? 'cloud',
    // Task 3 — absent means enabled (matches the backend's isAssistantEnabled
    // default). Same defensive-default shape as deploymentMode above.
    assistantEnabled: apiUser.assistantEnabled ?? true,
    preferences,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem(USER_KEY)

    return stored ? JSON.parse(stored) : null
  })
  const [authState, setAuthState] = useState<AuthState>('loading')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isAuthenticated = !!user

  const clearRefreshInterval = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
      refreshTimeoutRef.current = null
    }
  }, [])

  /**
   * End the session in REACT, not just in localStorage.
   *
   * `AuthGuard` renders on `isAuthenticated`, which is `!!user`, and runs its own `checkAuth()`
   * once on mount — so a session the provider has permanently rejected has to reach this state or
   * the guard keeps rendering the app behind a failing page.
   */
  const endSession = useCallback(() => {
    clearRefreshInterval()
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    setUser(null)
    setAuthState('unauthenticated')
  }, [clearRefreshInterval])

  /**
   * Refresh just BEFORE the token in hand expires, then re-arm from the new one.
   *
   * A self-rescheduling `setTimeout`, not a `setInterval`: the cadence is the provider's business
   * (about four minutes on Keycloak, about fifty-nine on Cognito) and the next delay can only be
   * computed once the new token exists. Re-arming also keeps the provider's SSO idle timer alive,
   * which is the half a fixed interval longer than that timeout can never do.
   */
  const setupTokenRefresh = useCallback(() => {
    clearRefreshInterval()

    const arm = (delay: number) => {
      refreshTimeoutRef.current = setTimeout(async () => {
        let newToken: string | null = null

        try {
          newToken = await authService.refreshToken()
        } catch {
          // A THROWN refresh is a transport failure — the provider rejected nothing. Both auth
          // clients answer `null` rather than throwing, so this is defence for a future one.
          newToken = null
        }

        if (newToken) {
          localStorage.setItem(TOKEN_KEY, newToken)
          arm(refreshDelayMs(newToken, Date.now()))

          return
        }

        // The refresh failed. The auth clients drop `gisify_refresh_token` ONLY on a definitive
        // rejection, so its absence is the provider-agnostic "this session cannot recover" signal —
        // the same one `services/api/client.ts` reads. Anything else (offline, 5xx, throttle) is
        // transient and gets a backed-off retry rather than a logout.
        //
        // Before this the failure was invisible to React entirely: the old `catch` branch only ran
        // on a THROW, and neither client throws, so nothing here could ever end a session.
        if (!localStorage.getItem(REFRESH_TOKEN_KEY)) {
          endSession()

          return
        }

        arm(TOKEN_REFRESH_RETRY_MS)
      }, delay)
    }

    arm(refreshDelayMs(localStorage.getItem(TOKEN_KEY), Date.now()))
  }, [clearRefreshInterval, endSession])

  /**
   * The api client discovered the session is dead mid-request. It can clear localStorage but cannot
   * reach this state, so it broadcasts instead. See `services/api/session-expired-emitter.ts`.
   */
  useEffect(() => subscribeSessionExpired(endSession), [endSession])

  const checkAuth = useCallback(async () => {
    // #218 — if a handoff cookie was set during a cross-subdomain redirect,
    // hydrate localStorage from it BEFORE we look up the token. The cookie is
    // parent-domain-scoped (.{baseDomain}) and one-shot — consumed and deleted
    // in the same call.
    consumeHandoffIntoLocalStorage()

    const token = localStorage.getItem(TOKEN_KEY)

    if (!token) {
      setUser(null)
      setAuthState('unauthenticated')

      return
    }

    try {
      setIsLoading(true)

      // Verify the user still exists in Cognito by refreshing the token.
      //
      // A falsy result is deliberately NOT treated as "log this session out".
      // The auth clients now keep `gisify_refresh_token` when the failure was
      // TRANSIENT (offline, Cognito 5xx, throttle) and only drop it on a
      // definitive rejection, so "refresh returned null" no longer implies a
      // dead session — the previous `else if (localStorage.getItem(...)) throw`
      // would have ended a perfectly valid session on a wifi blip at boot.
      // Fall through instead: `fetchUserProfile()` below still runs with the
      // token we hold, and a genuinely dead session 401s there and lands in the
      // catch, which is what ends it.
      const newToken = await authService.refreshToken()

      if (newToken) {
        localStorage.setItem(TOKEN_KEY, newToken)
      }

      const userProfile = await fetchUserProfile()
      localStorage.setItem(USER_KEY, JSON.stringify(userProfile))

      // #218 — also enforce on app boot, in case a tenant user has a stored
      // session and lands on the central root via a bookmark or shared URL.
      if (enforceTenantSubdomain(userProfile.tenantId)) return

      setUser(userProfile)
      setAuthState('authenticated')

      setupTokenRefresh()
    } catch (err) {
      // #218 — wrong tenant subdomain: redirect to the correct one with handoff.
      if (mapTenantMismatchError(err)) return

      if (mapTenantStatusError(err, setAuthState, setError)) {
        setUser(null)
        clearRefreshInterval()

        return
      }

      // Only a DEFINITIVE rejection may end the session — the same rule the
      // refresh path above follows, and the same provider-agnostic signal
      // `services/api/client.ts` reads: both auth clients drop
      // `gisify_refresh_token` exactly when the provider permanently rejected
      // it (NotAuthorizedException / invalid_grant / user gone) and keep it on
      // a transient one (offline, a Keycloak or Cognito 5xx, a throttle).
      //
      // Without this, a hard reload during a ~10s provider outage logged the
      // user out: the refresh round is never judged, so `fetchUserProfile()`
      // runs with the just-expired IdToken, 401s, and the catch-all below wiped
      // a session whose refresh token was intact and valid. Leave everything in
      // place instead — the cached profile keeps the app rendering and the next
      // refresh round (this request retried, or the next navigation) recovers
      // the live one. `authState` is deliberately NOT touched: we verified
      // nothing, so we claim nothing.
      if (localStorage.getItem(REFRESH_TOKEN_KEY)) {
        return
      }

      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(USER_KEY)
      setUser(null)
      setAuthState('unauthenticated')
      clearRefreshInterval()
    } finally {
      setIsLoading(false)
    }
  }, [setupTokenRefresh, clearRefreshInterval])

  const signIn = useCallback(
    async (email: string, password: string) => {
      try {
        setIsLoading(true)
        setError(null)
        const token = await authService.signIn(email, password)

        localStorage.setItem(TOKEN_KEY, token)

        // Rotate the browser's anon UUID before the first authed request so
        // the API Gateway custom authorizer can't serve a stale guest decision
        // it cached for this UUID earlier in the session (5-min TTL).
        rotateAnonymousId()

        const userProfile = await fetchUserProfile()
        localStorage.setItem(USER_KEY, JSON.stringify(userProfile))

        // #218 — tenant users signing in via the central root must be
        // redirected to their tenant subdomain. Halt local sign-in so we
        // don't briefly flash an authenticated state on the wrong host.
        if (enforceTenantSubdomain(userProfile.tenantId)) return

        setUser(userProfile)
        setAuthState('authenticated')

        setupTokenRefresh()
      } catch (err) {
        // Challenge errors — the caller (Login page) handles the prompt UI.
        // Don't poison error state with the challenge message.
        if (
          err instanceof authService.MfaChallengeError ||
          err instanceof authService.NewPasswordRequiredError ||
          err instanceof authService.PasswordUpdateRequiredError
        ) {
          throw err
        }

        // Issue #327A — Cognito-side "User is disabled" was confirmed by
        // probeTenantStatus to be a soft-deleted tenant. Drop local session,
        // flip authState so AuthGuard routes the user to /account-deleted.
        if (err instanceof authService.TenantDeletedError) {
          localStorage.removeItem(TOKEN_KEY)
          localStorage.removeItem(USER_KEY)
          setUser(null)
          setAuthState('tenant_deleted')
          setError(err.message)
          throw err
        }

        // Issue #259 — login on a non-existent tenant subdomain (or one
        // whose status probe returned unknown after a disabled-user error).
        if (err instanceof authService.TenantNotFoundError) {
          localStorage.removeItem(TOKEN_KEY)
          localStorage.removeItem(USER_KEY)
          setUser(null)
          setAuthState('tenant_not_found')
          setError(err.message)
          throw err
        }

        // #238 — auth succeeded at Cognito but the tenant is suspended/deleted.
        // Set a clear error message + drop the half-issued local session, then
        // throw so Login.tsx's catch keeps the user on the form (skipping the
        // navigate(from) that would otherwise silent-redirect).
        // #218 — wrong tenant subdomain: redirect to the correct one with handoff.
        if (mapTenantMismatchError(err)) return
        if (mapTenantStatusError(err, setAuthState, setError)) throw err

        const message = err instanceof Error ? err.message : 'Sign in failed'
        setError(message)
        throw err
      } finally {
        setIsLoading(false)
      }
    },
    [setupTokenRefresh],
  )

  const respondNewPasswordChallenge = useCallback(
    async (email: string, newPassword: string, session: string) => {
      try {
        setIsLoading(true)
        setError(null)
        const token = await authService.respondToNewPasswordChallenge(email, newPassword, session)

        localStorage.setItem(TOKEN_KEY, token)
        rotateAnonymousId()

        const userProfile = await fetchUserProfile()
        localStorage.setItem(USER_KEY, JSON.stringify(userProfile))

        if (enforceTenantSubdomain(userProfile.tenantId)) return

        setUser(userProfile)
        setAuthState('authenticated')

        setupTokenRefresh()
      } catch (err) {
        // If the new password sets up MFA next, forward the MFA challenge to the caller.
        if (err instanceof authService.MfaChallengeError) {
          throw err
        }

        // #218 — wrong tenant subdomain: redirect to the correct one with handoff.
        if (mapTenantMismatchError(err)) return
        if (mapTenantStatusError(err, setAuthState, setError)) throw err

        const message = err instanceof Error ? err.message : 'Set new password failed'
        setError(message)
        throw err
      } finally {
        setIsLoading(false)
      }
    },
    [setupTokenRefresh],
  )

  const respondMfaChallenge = useCallback(
    async (email: string, code: string, session: string) => {
      try {
        setIsLoading(true)
        setError(null)
        const token = await authService.respondToMfaChallenge(email, code, session)

        localStorage.setItem(TOKEN_KEY, token)
        rotateAnonymousId()

        const userProfile = await fetchUserProfile()
        localStorage.setItem(USER_KEY, JSON.stringify(userProfile))

        if (enforceTenantSubdomain(userProfile.tenantId)) return

        setUser(userProfile)
        setAuthState('authenticated')

        setupTokenRefresh()
      } catch (err) {
        // #218 — wrong tenant subdomain: redirect to the correct one with handoff.
        if (mapTenantMismatchError(err)) return
        if (mapTenantStatusError(err, setAuthState, setError)) throw err

        const message = err instanceof Error ? err.message : 'Sign in failed'
        setError(message)
        throw err
      } finally {
        setIsLoading(false)
      }
    },
    [setupTokenRefresh],
  )

  const signOut = useCallback(async () => {
    try {
      setIsLoading(true)
      await authService.signOut()
    } catch {
      // Ignore sign out errors
    } finally {
      clearRefreshInterval()
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(USER_KEY)
      localStorage.removeItem(REFRESH_TOKEN_KEY)
      // Mirror signIn: rotate anon UUID so the next anon request doesn't reuse
      // the authorizer's cached `kind: 'authenticated'` decision tied to the
      // previous session's UUID.
      rotateAnonymousId()
      setUser(null)
      setAuthState('unauthenticated')
      setIsLoading(false)
    }
  }, [clearRefreshInterval])

  const clearLocalSession = useCallback(() => {
    clearRefreshInterval()
    authService.clearStoredTokens()
    // Same reason as signOut: the authorizer cached decisions against the old anon UUID.
    rotateAnonymousId()
    setUser(null)
    setAuthState('unauthenticated')
  }, [clearRefreshInterval])

  const updatePreferences = useCallback((prefs: Partial<UserPreferences>) => {
    setUser(prev => {
      if (!prev) return prev
      const updated = {
        ...prev,
        preferences: { ...prev.preferences, ...prefs },
      }
      localStorage.setItem(USER_KEY, JSON.stringify(updated))

      return updated
    })
    // Fire-and-forget server persistence — theme toggle feels instant from the
    // optimistic local update above; a failed round-trip just means cross-device
    // sync is skipped for this change.
    authApi.updatePreferences(prefs).catch((err: unknown) => {
      // eslint-disable-next-line no-console
      console.warn('Failed to persist user preferences:', err)
    })
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const refreshUser = useCallback(async () => {
    try {
      const userProfile = await fetchUserProfile()
      localStorage.setItem(USER_KEY, JSON.stringify(userProfile))
      setUser(userProfile)
    } catch {
      // Silently ignore — the UI can function with stale data
    }
  }, [])

  useEffect(() => {
    const isDark = user?.preferences.darkMode ?? false

    document.documentElement.classList.toggle('dark', isDark)
    // UX L3 — set CSS color-scheme so user-agent widgets (the native
    // <select> chevron, scrollbars, form controls) draw with the OS dark
    // palette. Without this, Chrome on Linux paints the chevron arrow in
    // its light-theme color even when our `.dark` classes flip every
    // surrounding color — the chevron stands out as a bright glyph on
    // an otherwise-dark control. Documented limitation in apps/console
    // CLAUDE.md ("Known limitation — native <select> chevron"); this
    // closes it.
    document.documentElement.style.colorScheme = isDark ? 'dark' : 'light'
  }, [user?.preferences.darkMode])

  useEffect(() => {
    return () => {
      clearRefreshInterval()
    }
  }, [clearRefreshInterval])

  // #218 — Post-redirect bootstrap. AuthGuard runs checkAuth on protected routes,
  // but a handoff redirect lands on the destination subdomain at whatever path
  // the source page was on (typically /auth/login). On public routes there's no
  // AuthGuard, so the handoff cookie would sit unconsumed. Detect the cookie at
  // AuthProvider mount and run checkAuth inline so the destination console picks
  // up the just-handed-off session even when the path is public.
  useEffect(() => {
    if (consumeHandoffIntoLocalStorage()) {
      void checkAuth()
    }
    // checkAuth is stable via useCallback with stable deps — running this once
    // at mount is the intent (handoff cookies are one-shot anyway).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        authState,
        isLoading,
        error,
        signIn,
        respondMfaChallenge,
        respondNewPasswordChallenge,
        signOut,
        clearLocalSession,
        checkAuth,
        updatePreferences,
        clearError,
        refreshUser,
        logout: signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
